import React, { useState, useEffect } from "react";
import { Tag, Tabs } from "antd";
import { ContrastTag } from "../../../../components/shared";
import { FileTextOutlined, EditOutlined, PlusOutlined, MailOutlined, MessageOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/es/table";

import Layout from "../../../../components/Layout";
import PageContainer from "../../../../components/shared/PageContainer";
import PageToolbar from "../../../../components/shared/PageToolbar";
import { DataTable, StatusTag } from "../../../../components/shared";

import { Template, defaultTemplate, fetchTemplates } from "../../../../api/communications/templateApi";
import TemplateBuilderDrawer from "./TemplateBuilderDrawer";
import EmailSignaturesTab from "../EmailSignatureTab";

export const TemplatePage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
	const [loading, setLoading] = useState(false);
	const [templates, setTemplates] = useState<Template[]>([]);
	const [template, setTemplate] = useState<Template | null>(null);
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [activeTab, setActiveTab] = useState("templates");
	const [search, setSearch] = useState("");
	const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);

	useEffect(() => {
		loadTemplates("");
	}, []);

	const loadTemplates = async (q: string) => {
		setLoading(true);
		try {
			const { data } = await fetchTemplates(q);
			setTemplates(data);
		} finally {
			setLoading(false);
		}
	};

	const handleCreate = () => {
		setTemplate({ ...defaultTemplate, name: "", body: "", subject: "", type: "email" });
		setIsDrawerOpen(true);
	};

	const handleEdit = (t: Template) => {
		setTemplate(t);
		setIsDrawerOpen(true);
	};

	const handleDrawerClose = () => {
		setTemplate(null);
		setIsDrawerOpen(false);
		loadTemplates("");
	};

	const filtered = templates.filter((t) => {
		const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase());
		const matchType = !typeFilter || t.type === typeFilter;
		return matchSearch && matchType;
	});

	const typeIcon = (type: string) => {
		if (type === "email") return <MailOutlined style={{ fontSize: 11, marginRight: 3 }} />;
		if (type === "sms") return <MessageOutlined style={{ fontSize: 11, marginRight: 3 }} />;
		return null;
	};

	const typeColor: Record<string, string> = {
		email: "var(--mg-info)",
		sms: "var(--mg-success)",
		both: "var(--mg-primary)",
	};

	const columns: ColumnsType<Template> = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			sorter: (a, b) => a.name.localeCompare(b.name),
			render: (name: string, record: Template) => (
				<span>
					{name}
					{record.is_global && (
						<Tag color="purple" style={{ marginLeft: 8, fontSize: 11 }}>Global</Tag>
					)}
				</span>
			),
		},
		{
			title: "Category",
			dataIndex: "category",
			key: "category",
			sorter: (a, b) => (a.category || "").localeCompare(b.category || ""),
			render: (cat: string) => (
				<span style={{ fontSize: 12 }}>
					{cat?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
				</span>
			),
		},
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			width: 90,
			sorter: (a, b) => a.type.localeCompare(b.type),
			render: (type: string) => (
				<ContrastTag background={typeColor[type] || undefined} style={{ fontSize: 11 }}>
					{typeIcon(type)}
					{type.toUpperCase()}
				</ContrastTag>
			),
		},
		{
			title: "Subject",
			dataIndex: "subject",
			key: "subject",
			ellipsis: true,
			sorter: (a, b) => (a.subject || "").localeCompare(b.subject || ""),
		},
		{
			title: "Status",
			dataIndex: "is_active",
			key: "is_active",
			width: 100,
			render: (v: boolean) => <StatusTag status={v ? "active" : "inactive"} />,
		},
		{
			title: "Created",
			dataIndex: "created_at",
			key: "created_at",
			width: 120,
			sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
			render: (d: string) => (d ? dayjs(d).format("DD MMM YYYY") : "—"),
		},
	];

	const templatesTab = (
		<>
			<PageToolbar
				primaryAction={embedded ? { label: "New Template", icon: <PlusOutlined />, onClick: handleCreate } : undefined}
				filters={[
					{
						type: "search",
						placeholder: "Search templates…",
						value: search,
						onChange: (v) => setSearch(v),
						width: 260,
					},
					{
						type: "select",
						placeholder: "Filter by type",
						value: typeFilter,
						onChange: (v) => setTypeFilter(v),
						options: [
							{ label: "Email", value: "email" },
							{ label: "SMS", value: "sms" },
							{ label: "Both", value: "both" },
						],
						width: 150,
					},
				]}
				onReset={() => { setSearch(""); setTypeFilter(undefined); }}
			/>
			<DataTable
				columns={columns}
				dataSource={filtered}
				rowKey="id"
				loading={loading}
				onRowClick={handleEdit}
				emptyTitle="No templates yet"
				emptyDescription="Create your first notification template to get started"
				emptyAction={{ label: "New Template", onClick: handleCreate }}
			/>
		</>
	);

	const tabItems = [
		{
			key: "templates",
			label: <span><FileTextOutlined /> Notification Templates</span>,
			children: templatesTab,
		},
		{
			key: "signatures",
			label: <span><EditOutlined /> Email Signatures</span>,
			children: <EmailSignaturesTab />,
		},
	];

	if (isDrawerOpen && template) {
		// rendered portalled outside tab structure
	}

	const content = (
		<>
			<Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
			{isDrawerOpen && template && (
				<TemplateBuilderDrawer
					template={template}
					isOpen={isDrawerOpen}
					isSubmitting={loading}
					setIsSubmitting={setLoading}
					onClose={handleDrawerClose}
				/>
			)}
		</>
	);

	if (embedded) return content;

	return (
		<Layout>
			<PageContainer
				title="Notification Templates"
				subtitle="Manage email, SMS, and push notification templates"
				breadcrumbs={[{ label: "Settings" }, { label: "Notification Templates" }]}
				actions={activeTab === "templates" ? [{ label: "New Template", icon: <PlusOutlined />, onClick: handleCreate, type: "primary" }] : []}
			>
				{content}
			</PageContainer>
		</Layout>
	);
};

export default TemplatePage;
